import React from "react";

const TermsOfUse = () => (
  <main style={{ padding: "2rem 1rem", maxWidth: "900px", margin: "0 auto" }}>
    <h1>Terms of Use for Meal Plan Subscription Service</h1>
    <p><strong>Last Updated:</strong> July&nbsp;12,&nbsp;2025</p>

    <h2>1. Introduction</h2>
    <p>
      Welcome to <strong>a service operated by Oak Crew MB</strong> (“we,” “our,” or “us”), a meal
      plan subscription service operated by Oak Crew MB. By accessing or
      using our mobile application and website (collectively, the “Service”),
      you agree to be bound by these Terms of Use (“Terms”). If you do not agree
      with these Terms, please do not use the Service.
    </p>

    <h2>2. Eligibility</h2>
    <p>
      To use the Service, you must be at least 18&nbsp;years old. By accessing
      the Service, you represent and warrant that you meet this age requirement
      and have the legal capacity to enter into this agreement. We reserve the
      right to request proof of age to verify eligibility and may terminate
      accounts of users found to be under&nbsp;18 without notice.
    </p>

    <h2>3. Service Description</h2>
    <p>
      <strong>a service operated by Oak Crew MB</strong> provides personalized meal plans and recipes. Key features include:
    </p>
    <ul>
      <li>Customized meal plans based on dietary preferences and goals</li>
      <li>Recipe collections and step-by-step cooking instructions</li>
      <li>Nutritional information and guidance</li>
      <li>Mobile application functionality</li>
      <li>Subscription management features</li>
    </ul>

    <h2>4. Medical and Dietary Disclaimer</h2>
    <p>
      <strong>🚨 No Medical Advice:</strong> Our meal plans provide general
      nutritional information and suggestions only. They are not a substitute
      for professional medical advice, diagnosis, or treatment. Always consult
      a qualified healthcare professional or registered dietitian before making
      significant dietary changes, especially if you have health conditions,
      allergies, or special dietary requirements. Reliance on any information
      provided through the Service is at your own risk.
    </p>

    <h2>5. Subscription Terms &amp; Payment</h2>
    <h3>5.1 Subscription Plans</h3>
    <p>
      We offer monthly meal plan subscriptions for <strong>$9.99&nbsp;USD</strong> per month.
      Some plans may include a 7-day free trial, clearly indicated at sign-up.
    </p>

    <h3>5.2 Free Trial</h3>
    <p>
      When a free trial is offered, you will have access to the Service for
      seven days at no charge. Unless you cancel before the trial ends, your
      subscription automatically converts to a paid monthly subscription at the
      then-current rate of $9.99&nbsp;USD.
    </p>

    <h3>5.3 Payment Processing</h3>
    <p>
      Payments are processed through secure third-party processors. By
      subscribing, you authorize us to charge your payment method on a recurring
      monthly basis until you cancel.
    </p>

    <h3>5.4 Automatic Renewal</h3>
    <p>
      Your subscription renews each month on the same date you initially
      subscribed unless you cancel. You will be charged the monthly fee unless
      you cancel before the next billing cycle.
    </p>

    <h3>5.5 Price Changes</h3>
    <p>
      We may modify pricing with 30&nbsp;days’ advance notice via email or
      in-app notification. Continued use after a price change constitutes
      acceptance of the new pricing.
    </p>

    <h2>6. Cancellation and Refunds</h2>
    <h3>6.1 Cancellation Policy</h3>
    <p>
      You may cancel at any time by emailing&nbsp;
      <a href="mailto:support@oncodiary.com">support@oncodiary.com</a>. You will not be charged for any future billing cycles after cancellation is processed. Cancellation requests are processed within 24–48 hours of receipt.
    </p>

    <h3>6.2 Cancellation Timing</h3>
    <ul>
      <li><strong>During Free Trial:</strong> Cancel anytime during the 7-day free trial to avoid being charged.</li>
      <li><strong>Paid Subscription:</strong> Cancel anytime to stop future charges. Your access will continue until the end of your current billing period.</li>
    </ul>

    <h3>6.3 Refund Policy</h3>
    <ul>
      <li><strong>Free Trial Cancellations:</strong> No charges will be applied if you cancel during the free trial period.</li>
      <li><strong>Paid Subscriptions:</strong> We generally do not provide refunds for partial months of service already provided.</li>
      <li><strong>Exceptional Circumstances:</strong> Refunds may be considered on a case-by-case basis for technical issues, service disruptions, or billing errors when requested within 7 days of the charge.</li>
    </ul>

    <h3>6.4 Refund Process</h3>
    <p>
      To request a refund, contact <a href="mailto:support@oncodiary.com">support@oncodiary.com</a> with your account details and reason for the request. We will review your request and respond within 5–7 business days.
    </p>

    <h2>7. User Content &amp; Community Guidelines</h2>
    <h3>7.1 Acceptable Use</h3>
    <ul>
      <li>Do not post harmful, offensive, misleading, or illegal content.</li>
      <li>Do not provide medical advice unless you are a licensed professional.</li>
      <li>Do not promote competing products or services.</li>
      <li>Do not violate other users' privacy or rights.</li>
      <li>Do not use the Service for any unlawful purpose.</li>
    </ul>

    <h3>7.2 Content License</h3>
    <p>
      You retain ownership of any content you create. By posting content, you grant us a non-exclusive, royalty-free license to use, display, and distribute your content within the Service for operational purposes.
    </p>

    <h2>8. Privacy &amp; Data Protection</h2>
    <p>
      We collect and process your data as outlined in our Privacy Policy, including:
    </p>
    <ul>
      <li>Dietary preferences and meal plan usage</li>
      <li>Account information and payment details</li>
      <li>Service usage analytics</li>
      <li>Communication preferences</li>
    </ul>
    <p>
      You have rights under applicable privacy laws to access, correct, or delete your personal data by contacting <a href="mailto:info@oncodiary.com">info@oncodiary.com</a>.
    </p>

    <h2>9. Intellectual Property</h2>
    <p>
      The Service, including meal plans, recipes, content, design, and trademarks, is owned by Oak Crew MB and protected by intellectual property laws. You may not copy, modify, or distribute any part of the Service without our prior written consent.
    </p>

    <h2>10. Limitation of Liability</h2>
    <p>
      <strong>🚫 Service Provided "As Is":</strong> The Service is provided without warranties of any kind. We do not guarantee uninterrupted or error-free service.
    </p>
    <p>
      <strong>📌 We Are Not Liable For:</strong>
    </p>
    <ul>
      <li>Dietary decisions made based on our meal plans</li>
      <li>Allergic reactions or health issues related to following our recommendations</li>
      <li>Technical issues or service interruptions</li>
      <li>Third-party service problems</li>
    </ul>
    <p>
      To the fullest extent permitted by law, our liability is limited to the amount you paid for the Service in the preceding 12 months.
    </p>

    <h2>11. Account Termination</h2>
    <h3>11.1 By You</h3>
    <p>
      You may cancel your account at any time by contacting <a href="mailto:support@oncodiary.com">support@oncodiary.com</a>.
    </p>
    <h3>11.2 By Us</h3>
    <p>
      We may suspend or terminate your account if you violate these Terms, engage in harmful activity, or attempt fraudulent payments.
    </p>
    <h3>11.3 Effect of Termination</h3>
    <p>
      Upon termination, your access to the Service will end, and any remaining subscription time will be forfeited unless otherwise required by law.
    </p>

    <h2>12. Force Majeure</h2>
    <p>
      We are not liable for delays or failures in service due to events beyond our reasonable control, such as natural disasters, wars, pandemics, or technical failures.
    </p>

    <h2>13. Governing Law &amp; Dispute Resolution</h2>
    <p>
      These Terms are governed by the laws of Lithuania. Disputes will be resolved through good faith negotiation, or if necessary, through binding arbitration in Lithuania.
    </p>

    <h2>14. Changes to Terms</h2>
    <p>
      We may update these Terms periodically. Significant changes will be communicated via email or in-app notification at least 30 days before taking effect. Continued use after changes constitutes acceptance.
    </p>

    <h2>15. Contact Information</h2>
    <p>
      For questions about these Terms or to cancel your subscription:
    </p>
    <ul>
      <li><strong>Email (Support):</strong> <a href="mailto:support@oncodiary.com">support@oncodiary.com</a></li>
      <li><strong>Email (General Inquiries):</strong> <a href="mailto:info@oncodiary.com">info@oncodiary.com</a></li>
    </ul>
  </main>
);

export default TermsOfUse;
